import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
// tslint:disable-next-line
import {
	ResidualsDetailByCategory,
	ResidualsSummaryCategories,
	ResidualsACHDetailByCategory,
	IResidualsDetailByMetric,
	HistoricalCommission,
	ResidualsDetailByCategoryDetail,
	AffiliateData,
	MerchantAffiliateData,
	SalesEntityModelList,
	ResidualsAgentSummary,
} from "../../shared/models/EvoSalesPortalModel";
import { Observable } from "rxjs";
import { KPIData } from "../../shared/models/EvoSalesPortalModel";
import { CacheService } from "../../shared/services/cache.service";
import { KeyValuePair } from "../../shared/classes/key-value-pair";
import { ResidualsDetailContext } from "./ResidualsDetailContext";
import { Router } from "@angular/router";
import {
	ResidualsAgentSummaryByRelationshipTypeRecord,
	ResidualsMerchantDetailByCategoryRecord,
	ResidualsMerchantDetailSummaryByRelationshipTypeRecord,
	ResidualsMerchantSummaryByRelationshipAgentRecord,
} from "app/shared/models/Residuals";
import { KeyValue } from "@angular/common";

/**
 * @summary
 * The service that handles data storage, data fetches, and parsing of data for all things residuals.
 * @description At a high level, residuals has two views (plus historical downloads)
 * - ISO or AP view determined by the chosen sales entity's type, once chosen the given view iterates over the categories for that type, and fetches and stores data into detail contexts.
 * - The fetching of data is designed to be async awaited 1 at a time to reduce strain on the DB tables which lock when read
 * </pre>
 */
@Injectable()
export class ResidualsService {
	controller = "api/residuals/";

	constructor(
		private http: HttpClient,
		private cacheService: CacheService,
		private router: Router
	) {}

	/**
	 * Unused, unsure what this is for
	 * @param type
	 * @param input
	 */
	toStronglyTyped<I, T>(type: { new (I): T }, input: Array<any>) {
		if (input) {
			input.forEach((item: I, index, array) => {
				const stronglyTyped = new type(item);
				array[index] = stronglyTyped;
			});
		}
	}

	/**
	 * Gets the list of categories for the residuals screen which is then used to fetch data per category 1 at a time
	 */
	getCategories(): Observable<Array<ResidualsSummaryCategories>> {
		const url = this.controller + "GetCategories";
		return this.cacheService.get(url, this.http.get(url));
	}

	/**
	 * Downloads the general Residuals Summary
	 * @param salesEntityID The selected SE
	 * @param commissionsDate The selected commissions date
	 */
	public GetResidualsSummaryDownload(
		salesEntityID: number,
		filetype: number,
		commissionsDate: Date
	) {
		const url = this.controller + "GetResidualsSummaryDownload";
		let toSubmit = new Array<KeyValuePair<string, string>>();
		toSubmit.push(
			new KeyValuePair<string, string>(
				"salesEntityID",
				salesEntityID.toString()
			)
		);
		toSubmit.push(
			new KeyValuePair<string, string>(
				"commissionsDate",
				this.BuildDateString(commissionsDate)
			)
		);
		toSubmit.push(
			new KeyValuePair<string, string>("filetype", filetype.toString())
		); //1 is excel for now

		let form = this.BuildHiddenFormForDownload(url, toSubmit);
		var div = document.getElementById("appendContractFormHere");
		div.appendChild(form);
		form.submit();
	}
	/**
	 * Downloads the Residuals AP Detail
	 * @param salesEntityID The selected SE
	 * @param commissionsDate The selected commissions date
	 */
	public GetResidualsAPDetailDownload(
		salesEntityID: number,
		commissionsDate: Date
	) {
		const url = this.controller + "GetAPSummaryDownload";
		let toSubmit = new Array<KeyValuePair<string, string>>();
		toSubmit.push(
			new KeyValuePair<string, string>(
				"salesEntityID",
				salesEntityID.toString()
			)
		);
		toSubmit.push(
			new KeyValuePair<string, string>(
				"commissionsDate",
				this.BuildDateString(commissionsDate)
			)
		);

		let form = this.BuildHiddenFormForDownload(url, toSubmit);
		var div = document.getElementById("appendContractFormHere");
		div.appendChild(form);
		form.submit();
	}

	/**
	 * Downloads the Residuals ISO Detail
	 * @param salesEntityID The selected SE
	 * @param commissionsDate The selected commissions date
	 */
	public GetResidualsISODetailDownload(
		salesEntityID: number,
		commissionsDate: Date
	) {
		const url = this.controller + "GetISODetailDownload";
		let toSubmit = new Array<KeyValuePair<string, string>>();
		toSubmit.push(
			new KeyValuePair<string, string>(
				"salesEntityID",
				salesEntityID.toString()
			)
		);
		toSubmit.push(
			new KeyValuePair<string, string>(
				"commissionsDate",
				this.BuildDateString(commissionsDate)
			)
		);

		let form = this.BuildHiddenFormForDownload(url, toSubmit);
		var div = document.getElementById("appendContractFormHere");
		div.appendChild(form);
		form.submit();
	}

	/**
	 * Downloads the Residuals ISO Summary
	 * @param salesEntityID The selected SE
	 * @param commissionsDate The selected commissions date
	 */
	public GetResidualsISOSummaryDownload(
		salesEntityID: number,
		commissionsDate: Date
	) {
		const url = this.controller + "GetISOSummaryDownload";
		let toSubmit = new Array<KeyValuePair<string, string>>();
		toSubmit.push(
			new KeyValuePair<string, string>(
				"salesEntityID",
				salesEntityID.toString()
			)
		);
		toSubmit.push(
			new KeyValuePair<string, string>(
				"commissionsDate",
				this.BuildDateString(commissionsDate)
			)
		);

		let form = this.BuildHiddenFormForDownload(url, toSubmit);
		var div = document.getElementById("appendContractFormHere");
		div.appendChild(form);
		form.submit();
	}

	/**
	 * The original call to get residuals data (iterated on per category) async returns promise
	 * @param salesEntityID The selected SE
	 * @param categoryID The given category ID
	 * @param commissionsDate The selected commissions date
	 */
	async getResiduals(
		salesEntityID: number,
		categoryID: number,
		commissionsDate: Date
	): Promise<ResidualsDetailByCategory> {
		const url = this.controller + "GetResiduals";

		const parms = new HttpParams()
			.set("salesEntityID", salesEntityID.toString())
			.append("categoryID", categoryID.toString())
			.append(
				"commissionsDate",
				encodeURI(this.BuildDateString(commissionsDate))
			);
		const observable = this.cacheService.get(
			url + parms,
			this.http.get(url, { params: parms })
		);

		return observable.toPromise();
	}

	/**
	 * Gets residuals by metric for the mid drilldown
	 * @param salesEntityID The selected SE
	 * @param categoryID The given category ID
	 * @param metricID The given metric
	 * @param commissionsDate The selected commissions date
	 */
	async getResidualsByMetricID(
		salesEntityID: number,
		categoryID: number,
		metricID: number,
		commissionsDate: Date
	): Promise<IResidualsDetailByMetric> {
		const url = this.controller + "GetResidualsByMetric";
		const parms = new HttpParams()
			.set("salesEntityID", salesEntityID.toString())
			.append("categoryID", categoryID.toString())
			.append("metricID", metricID.toString())
			.append(
				"commissionsDate",
				encodeURI(this.BuildDateString(commissionsDate))
			);
		const observable = this.cacheService.get(
			url + parms,
			this.http.get(url, { params: parms })
		);
		return observable.toPromise();
	}

	/**
	 * Gets KPI data using the top level selections
	 * @param salesEntityID The selected SE
	 * @param commissionsDate The selected commissions date
	 */
	getKpiData(
		salesEntityID: number,
		commissionsDate: Date
	): Observable<KPIData> {
		const url = this.controller + "GetKpiData";
		const parms = new HttpParams()
			.set("salesEntityID", salesEntityID.toString())
			.append(
				"commissionsDate",
				encodeURI(this.BuildDateString(commissionsDate))
			);
		const observable = this.cacheService.get(
			url + parms,
			this.http.get(url, { params: parms })
		);

		return observable;
	}

	/**
	 * Gets agent summary relationship types with required params, returns observable
	 * @param salesEntityID The selected SE
	 * @param commissionsDate The selected commissions date
	 */
	GetAgentSummaryRelationshipTypes(
		salesEntityID: number,
		commissionsDate: Date
	): Observable<any> {
		const url = this.controller + "GetAgentSummaryRelationshipTypes";
		const parms = new HttpParams()
			.set("salesEntityID", salesEntityID.toString())
			.append(
				"commissionsDate",
				encodeURI(this.BuildDateString(commissionsDate))
			);
		const observable = this.cacheService.get(
			url + parms,
			this.http.get(url, { params: parms })
		);
		return observable;
	}

	/**
	 * Gets agent summary by relationship type with required parameters, async returns promise
	 * @param salesEntityID The selected SE
	 * @param relationshipTypeID The selected relationship type ID
	 * @param commissionsDate The selected commissions date
	 */
	async GetAgentSummaryByRelationshipType(
		salesEntityID: number,
		relationshipTypeID: number,
		commissionsDate: Date
	): Promise<any> {
		const url = this.controller + "GetAgentSummaryByRelationshipType";
		const parms = new HttpParams()
			.set("salesEntityID", salesEntityID.toString())
			.append("relationshipTypeID", relationshipTypeID.toString())
			.append(
				"commissionsDate",
				encodeURI(this.BuildDateString(commissionsDate))
			);
		const observable = this.cacheService.get(
			url + parms,
			this.http.get(url, { params: parms })
		);
		return observable.toPromise();
	}

	/**
	 * Gets merchant summary by relationshiptype and agent with required parameters, async returns promise
	 * @param salesEntityID The selected SE
	 * @param agentSalesEntityID The selected AGENT sales entity ID
	 * @param relationshipTypeID The selected relationship type ID
	 * @param commissionsDate The selected commissions date
	 */
	async GetMerchantSummaryByRelationshipAgent(
		salesEntityID: number,
		agentSalesEntityID: number,
		relationshipTypeID: number,
		commissionsDate: Date
	): Promise<any> {
		const url = this.controller + "GetMerchantSummaryByRelationshipAgent";
		const parms = new HttpParams()
			.set("salesEntityID", salesEntityID.toString())
			.append("agentSalesEntityID", agentSalesEntityID.toString())
			.append("relationshipTypeID", relationshipTypeID.toString())
			.append(
				"commissionsDate",
				encodeURI(this.BuildDateString(commissionsDate))
			);
		const observable = this.cacheService.get(
			url + parms,
			this.http.get(url, { params: parms })
		);
		return observable.toPromise();
	}

	/**
	 * Gets merchant detail summary by Relationshiptype, misnomer, because it returns them by relationship type
	 * async returns promise
	 * @param salesEntityID The selected SE
	 * @param merchantNumber The selected merchant number (Different than MERCHANTID)
	 * @param commissionsDate The selected commissions date
	 */
	async GetMerchantDetailSummaryByRelationshipType(
		salesEntityID: number,
		merchantNumber: number,
		commissionsDate: Date
	): Promise<Array<ResidualsMerchantDetailSummaryByRelationshipTypeRecord>> {
		const url =
			this.controller + "GetMerchantDetailSummaryByRelationshipType";
		const parms = new HttpParams()
			.set("salesEntityID", salesEntityID.toString())
			.append("merchantNumber", merchantNumber.toString())
			.append(
				"commissionsDate",
				encodeURI(this.BuildDateString(commissionsDate))
			);
		const observable = this.cacheService.get(
			url + parms,
			this.http.get(url, { params: parms })
		);
		return observable.toPromise();
	}

	/**
	 * Gets merchant detail by selected category using required parameters async returns a promise
	 * @param salesEntityID The selected SE
	 * @param merchantNumber The selected merchant number (Different than MERCHANTID)
	 * @param categoryID The selected category ID
	 * @param commissionsDate The selected commissions date
	 */
	async GetMerchantDetailByCategory(
		salesEntityID: number,
		merchantNumber: number,
		categoryID: number,
		commissionsDate: Date
	): Promise<Array<ResidualsMerchantDetailByCategoryRecord>> {
		const url = this.controller + "GetMerchantDetailByCategory";
		const parms = new HttpParams()
			.set("salesEntityID", salesEntityID.toString())
			.append("merchantNumber", merchantNumber.toString())
			.append("categoryID", categoryID.toString())
			.append(
				"commissionsDate",
				encodeURI(this.BuildDateString(commissionsDate))
			);
		const observable = this.cacheService.get(
			url + parms,
			this.http.get(url, { params: parms })
		);
		return observable.toPromise();
	}

	/**
	 * Requests ACH detail using the required parameters async returns a promise
	 * @param salesEntityID The selected SE
	 * @param categoryID The selected category
	 * @param commissionsDate The selected commissions date
	 */
	async getResidualsACHDetail(
		salesEntityID: number,
		categoryID: number,
		commissionsDate: Date
	): Promise<ResidualsACHDetailByCategory> {
		const url = this.controller + "GetResidualsACHDetail";

		const parms = new HttpParams()
			.set("salesEntityID", salesEntityID.toString())
			.append("categoryID", categoryID.toString())
			.append(
				"commissionsDate",
				encodeURI(this.BuildDateString(commissionsDate))
			);
		const observable = this.cacheService.get(
			url + parms,
			this.http.get(url, { params: parms })
		);

		return observable.toPromise();
	}

	/**
	 * Fetches a list of available historical commissions downloads.
	 * @param salesEntityID SE_ID
	 * @param commissionsDate Date of Commissions
	 */
	public GetHistoricalCommissionsList(
		salesEntityID: number,
		commissionsDate: Date
	): Promise<Array<HistoricalCommission>> {
		const url = this.controller + "GetHistoricalCommisionsList";
		const parms = new HttpParams()
			.set("salesEntityID", salesEntityID.toString())
			.append(
				"commissionsDate",
				encodeURI(this.BuildDateString(commissionsDate))
			);
		const observable = this.cacheService.get(
			url + parms,
			this.http.get(url, { params: parms })
		);
		return observable.toPromise();
	}

	/**
	 * Fetches and downloads historical commissions file by file ID.
	 * @param fid file ID
	 */
	public GetHistoricalCommissionDownload(fid: number) {
		let params: Array<KeyValuePair<string, string>> = new Array<
			KeyValuePair<string, string>
		>();
		params.push(new KeyValuePair<string, string>("fid", fid.toString()));
		//Call and get the download file
		const url = this.controller + "GetHistoricalCommisionsFile";
		let form = this.BuildHiddenFormForDownload(url, params);
		var div = document.getElementById("appendContractFormHere");
		div.appendChild(form);
		form.submit();
	}

	/**
	 * Fetches a list of affiliates.
	 * @param salesEntityID SE_ID
	 * @param commissionsDate Date of Commissions
	 */
	public GetAffiliatesData(
		salesEntityID: number,
		commissionsDate: Date
	): Promise<Array<AffiliateData>> {
		const url = this.controller + "GetAffiliatesData";
		const parms = new HttpParams()
			.set("salesEntityID", salesEntityID.toString())
			.append(
				"commissionsDate",
				encodeURI(this.BuildDateString(commissionsDate))
			);
		const observable = this.cacheService.get(
			url + parms,
			this.http.get(url, { params: parms })
		);
		return observable.toPromise();
	}

	/**
	 * Fetches a list of merchant affiliates.
	 * @param salesEntityID SE_ID
	 * @param commissionsDate Date of Commissions
	 */
	public GetMerchantAffiliatesData(
		affiliateSalesEntityID: number,
		salesEntityID: number
	): Promise<Array<MerchantAffiliateData>> {
		const url = this.controller + "GetMerchantAffiliatesData";
		const commisionsDate = new Date(
			this.SelectedYear,
			this.SelectedMonth,
			0
		);
		const parms = new HttpParams()
			.set("affiliateSalesEntityID", affiliateSalesEntityID.toString())
			.append("salesEntityID", salesEntityID.toString())
			.append(
				"commissionsDate",
				encodeURI(this.BuildDateString(commisionsDate))
			);
		const observable = this.cacheService.get(
			url + parms,
			this.http.get(url, { params: parms })
		);
		return observable.toPromise();
	}

	/**
	 * Formats a JS date object to string with proper month value.
	 * Input JS Date -> output ex "1/1/2021"
	 * @param d A javascript date to format to string
	 */
	private BuildDateString(d: Date): string {
		let retval: string = "";
		retval = retval + (d.getMonth() + 1).toString();
		retval = retval + "/";
		retval = retval + d.getDate().toString();
		retval = retval + "/";
		retval = retval + d.getFullYear().toString();
		// console.log("DateString:", retval);
		return retval;
	}

	//Maintains selections for drilldowns (externally stored from the component)
	public SelectedSalesEntityID: number;
	public SelectedMonth: number;
	public SelectedYear: number;
	public seType: number;

	//Shareables
	public backendKey = "EVO Backend Processing Summary";
	public frontendKey = "Front End Transaction Fees";
	public miscKey = "Miscellaneous Fees Summary";
	public achRejectsKey = "ACH Net Reject Activity";
	public adjustmentsKey = "Residual Adjustment";
	public binReserveKey = "Bin Sponsorship";
	public affiliatesKey = "Affiliates";
	public agentSummaryKey = "Summary";
	public residualsAgentSummaryKey = "Residuals Agent Summary";
	public parentDrilledCategory: KeyValuePair<string, number>;
	public drilledCategory: string; //Used by {all?} drill downs for table name
	public drilledIsoItem: ResidualsAgentSummaryByRelationshipTypeRecord; //Used for ISO drilldowns only to store what agent is being drilled
	public breadcrumbs: Array<KeyValuePair<string, number>>;
	public salesEntityTreeModelForAffiliatesPage: SalesEntityModelList;

	/**
	 * Updates the breadcrumbs by removing crumbs beyond the ID of the clicked crumb.
	 * The ID is not the index, but the ID number of the crumb ex: Sales entity/Residuals Category
	 * @param id ID of the breadcrumb that was clicked
	 */
	updateBreadcrumbs(id: number) {
		//remove everything from the list of crumbs down the tree
		console.log("Breadcrumbs Updating: ", this.breadcrumbs);
		//If they send in the root node, it's empty
		if (id === undefined) {
			this.breadcrumbs = new Array<KeyValuePair<string, number>>();
		}
		this.breadcrumbs.find((x) => x.value == id);
		for (var _i = 0; _i < this.breadcrumbs.length - 1; _i++) {
			if (this.breadcrumbs[_i].value == id) {
				if (_i == 0) {
					//single element array
					this.breadcrumbs = [this.breadcrumbs[0]];
				} else {
					this.breadcrumbs = this.breadcrumbs.slice(0, _i + 1);
				}
				break;
			}
		}
		console.log("Breadcrumbs Updated: ", this.breadcrumbs);
	}

	/**
	 * Adds a crumb to the end of the breadcrumbs with the passed in ID and Name (name will be displayed)
	 * @param categoryID The residuals categoryID to identify the breadcrumb
	 * @param categoryName The category name to display on the breadcrumb trail
	 */
	addBreadcrumb(categoryID: number, categoryName: string) {
		//need a string value for the category
		let toPush = new KeyValuePair<string, number>(categoryName, categoryID);
		this.breadcrumbs.push(toPush);
		this.breadcrumbs = Object.assign(
			new Array<KeyValuePair<string, number>>(),
			this.breadcrumbs
		);
	}

	/**
	 * Populates the passed in array by reference with Jan-Dec and their proper number 1-12
	 * @param _months Array of KVP to populate with months
	 */
	getMonths(_months: Array<KeyValuePair<string, number>>) {
		if (0 === _months.length) {
			let month = 0;
			_months.push(new KeyValuePair<string, number>("January", ++month));
			_months.push(new KeyValuePair<string, number>("February", ++month));
			_months.push(new KeyValuePair<string, number>("March", ++month));
			_months.push(new KeyValuePair<string, number>("April", ++month));
			_months.push(new KeyValuePair<string, number>("May", ++month));
			_months.push(new KeyValuePair<string, number>("June", ++month));
			_months.push(new KeyValuePair<string, number>("July", ++month));
			_months.push(new KeyValuePair<string, number>("August", ++month));
			_months.push(
				new KeyValuePair<string, number>("September", ++month)
			);
			_months.push(new KeyValuePair<string, number>("October", ++month));
			_months.push(new KeyValuePair<string, number>("November", ++month));
			_months.push(new KeyValuePair<string, number>("December", ++month));
		}
	} // End getMonths

	/**
	 * Populates KVP of years from the last completed 18months (if it's empty)
	 * @param _years Takes an empty array of KVPs - display values and values (2018 with value 2018)
	 */
	getYears(_years: Array<KeyValuePair<number, number>>) {
		if (0 === _years.length) {
			let curYear: number;
			for (let i = 1; i <= 18; i++) {
				var d = new Date();
				d.setMonth(d.getMonth() - i);
				let year: number = d.getFullYear();
				//console.log(d);
				if (curYear !== year) {
					curYear = year;
					_years.push(new KeyValuePair<number, number>(year, year));
				}
			} //End of For
		}
	} // End getYears

	/**
	 * Makes a date from the selections setting it to last day of selected month, assumes you pass real month index (1=Jan)
	 * @param selectedYear Selected year ex: 2000
	 * @param selectedMonth Selected month ex: 1 (which assumes january)
	 */
	getEndOfLastMonthDate(selectedYear: number, selectedMonth: number): Date {
		let date = new Date(selectedYear, selectedMonth, 0);
		//console.log("Selected Month Last Day in Rsvc:", date);
		return date;
	}

	/**
	 * Returns true if the category is ACH or the detail
	 * @param category the category:ResidualsDetailByCategoryDetail from the list
	 * @param context the context:ResidualsDetailContext for this summary
	 */
	public canDrill(
		category: ResidualsDetailByCategoryDetail,
		context: ResidualsDetailContext
	): boolean {
		if (
			([30, 31, 32, 33].indexOf(category.CategoryID) > -1 ||
				!context.details.Detail ||
				(context.details.Detail && category.HasMerchantDetail)) &&
			!this.isTotal(category.CategoryName) &&
			category.CategoryName.toUpperCase() !== "ACH SERVICE FEE"
		) {
			//If it's an ACH they can all drill but use a different route
			//If it's no detail then they can drill into the same call (get residuals)
			//If it's detail + mid then drill into MID level
			return true;
		} else {
			//If it's none of these then no drilling
			return false;
		}
	}

	/**
	 * Returns true if the passed in name.toUpper is TOTALS otherwise returns false
	 * @param catName Category Name ex: "BIN SPONSORSHIP" or "TOTALS"
	 */
	public isTotal(catName: string): boolean {
		if (catName.toUpperCase() === "TOTALS") {
			return true;
		}
		return false;
	}

	/**
	 * Will drill to the next level if appropriate and determine where the next level is
	 * @param category The category:ResidualsDetailByCategoryDetail in this summary
	 * @param context The residualsDetailContext for this summary
	 */
	drillTo(
		category: ResidualsDetailByCategoryDetail,
		context: ResidualsDetailContext,
		fromSummary: boolean = false
	) {
		console.log("drilling");
		// console.log(context);
		// console.log(category);
		console.log(context.category);
		if (fromSummary) {
			//If we drilled here from the first level use the context to add a preemptive breadcrumb
			const tempkvp = new KeyValuePair<string, number>(
				context.category.CategoryName,
				context.category.CategoryID
			);
			this.parentDrilledCategory = tempkvp;
			//Anytime we leave the summary page reset the breadcrumb trail
			this.breadcrumbs = new Array<KeyValuePair<string, number>>();
			let firstCrumb = this.parentDrilledCategory;
			this.breadcrumbs.push(firstCrumb);
			let secondCrumb = new KeyValuePair<string, number>(
				category.CategoryName,
				category.CategoryID
			);
			this.breadcrumbs.push(secondCrumb);
		} else {
			//We're at least 1 deep drilling into a category so add the next crumb from this category we're clicking
			const tempkvp = new KeyValuePair<string, number>(
				category.CategoryName,
				category.CategoryID
			);
			this.parentDrilledCategory = tempkvp;
			//Add a crumb for what we just did
			this.addBreadcrumb(category.CategoryID, category.CategoryName);
		}
		this.drilledCategory = category.CategoryName; // Pass this to the next level
		if ([30, 31, 32, 33].indexOf(category.CategoryID) > -1) {
			//If it's an ACH they can all drill but use a different route
			this.router.navigate([
				"/residuals/achdrilldown/" + category.CategoryID.toString(),
			]);
		} else if (!context.details.Detail) {
			//If it's no detail then they can drill into the same call (get residuals)
			this.router.navigate([
				"/residuals/commondrilldown/" + category.CategoryID.toString(),
			]);
		} else if (context.details.Detail && category.HasMerchantDetail) {
			//If it's detail + mid then drill into MID level
			let cat: number;
			if (!fromSummary) {
				//If there's breadcrumbs use them...otherwise just default to the current overarching category
				cat = this.breadcrumbs[this.breadcrumbs.length - 1].value;
			} else {
				cat = context.category.CategoryID;
				//We're coming direct to MID from the top level and need to add the crumbs ourselves
				this.breadcrumbs = new Array<KeyValuePair<string, number>>();
				let firstCrumb = this.parentDrilledCategory;
				this.breadcrumbs.push(firstCrumb);
				// let secondCrumb = new KeyValuePair<string, number>(category.CategoryName, category.CategoryID);
				// this.breadcrumbs.push(secondCrumb);
			}

			this.router.navigate([
				"/residuals/middrilldown/" +
					cat +
					"/" +
					category.CategoryID.toString(),
			]);
		}
		return;
	}

	/**
	 * Drills to the new screen for affiliates, adds the associated breadcrumbs
	 * @param affiliate Affiliate data from the clicked row
	 * @param context unused??
	 * @param fromSummary True if the user drilled from the summary screen, false if drilling from the strange sub-summary-summary-view table
	 */
	drillToAgentMerchantList(
		affiliate: AffiliateData,
		context: ResidualsDetailContext,
		fromSummary: boolean = false
	) {
		if (fromSummary) {
			//If we drilled here from the first level use the context to add a preemptive breadcrumb
			const tempkvp = new KeyValuePair<string, number>("Affiliates", 1);
			this.parentDrilledCategory = tempkvp;
			//Anytime we leave the summary page reset the breadcrumb trail
			this.breadcrumbs = new Array<KeyValuePair<string, number>>();
			let firstCrumb = this.parentDrilledCategory;
			this.breadcrumbs.push(firstCrumb);
			let secondCrumb = new KeyValuePair<string, number>(
				affiliate.Description,
				affiliate.AffiliateSalesEntityID
			);
			this.breadcrumbs.push(secondCrumb);
		} else {
			//We're at least 1 deep drilling into a category so add the next crumb from this category we're clicking
			const tempkvp = new KeyValuePair<string, number>(
				affiliate.Description,
				affiliate.AffiliateSalesEntityID
			);
			this.parentDrilledCategory = tempkvp;
			//Add a crumb for what we just did
			this.addBreadcrumb(affiliate.AffiliateSalesEntityID, "Affiliate");
		}

		this.router.navigate([
			"/residuals/affiliatedrilldown/" +
				affiliate.AffiliateSalesEntityID.toString(),
		]);
	}

	/**
	 * Drills from the ISO summary
	 * @param item Clicked item to drill into
	 * @param context ResidualsDetailContext needed to create breadcrumbs
	 */
	isoDrillDown(
		item: ResidualsAgentSummaryByRelationshipTypeRecord,
		context: ResidualsDetailContext
	) {
		if (
			context.isoDetails &&
			context.isoDetails.Detail &&
			context.isoDetails.Detail.length > 0
		) {
			//If it's ISO residuals (from the summary) they also can drill, but to a different

			const tempkvp = new KeyValuePair<string, number>(
				context.isoDetails.RelationshipType,
				context.isoDetails.RelationshipTypeID
			);
			this.parentDrilledCategory = tempkvp;
			this.drilledCategory = item.Description;
			this.drilledIsoItem = item;
			//Anytime we leave the summary page reset the breadcrumb trail
			if (!this.breadcrumbs) {
				this.breadcrumbs = new Array<KeyValuePair<string, number>>();
			}

			//If the person is using the drilldown page to go back and forth they might already have the first crumb (prevent dupes)
			let firstCrumb = this.parentDrilledCategory;
			if (this.nonDuplicateCrumb(this.parentDrilledCategory)) {
				this.breadcrumbs.push(firstCrumb);
			}

			let secondCrumb = new KeyValuePair<string, number>(
				item.Description,
				item.AgentSalesEntityID
			);
			if (this.nonDuplicateCrumb(secondCrumb)) {
				this.breadcrumbs.push(secondCrumb);
			}
			console.log(
				"ISODrilldown",
				item,
				context,
				context.isoDetails.RelationshipTypeID
			);
			console.log("Breadcrumbs:", this.breadcrumbs);
			this.router.navigate([
				"/residuals/isomiddrilldown/" +
					context.isoDetails.RelationshipTypeID.toString() +
					"/" +
					this.drilledIsoItem.AgentSalesEntityID.toString(),
			]);
		}
	}

	/**
	 * Drills into the merchant summary
	 * @param item clicked item to drill into
	 * @param context ResidualsDetailContext needed to create breadcrumbs (TODO: UNUSED??)
	 */
	merchantSummaryDrillDown(
		item: ResidualsMerchantSummaryByRelationshipAgentRecord,
		context: ResidualsDetailContext
	) {
		//We know when this is called we're at least 2 crumbs deep (excluding root)
		//Add the third crumb
		let secondCrumb = new KeyValuePair<string, number>(
			item.DBA,
			item.MerchantNumber
		);
		if (this.nonDuplicateCrumb(secondCrumb)) {
			this.breadcrumbs.push(secondCrumb);
		}
		//Set the new page table header
		this.drilledCategory = item.DBA;
		console.log("Breadcrumbs:", this.breadcrumbs);
		//Navidate to the final page
		this.router.navigate([
			"/residuals/isomerchantsummary/" + item.MerchantNumber.toString(),
		]);
	}

	/**
	 * Returns true if the breadcrumb does not exist in the list.
	 * @param entry crumb to compare
	 */
	nonDuplicateCrumb(entry: KeyValuePair<string, number>) {
		this.breadcrumbs.forEach((element) => {
			if (element === entry) {
				return false;
			}
		});
		return true;
	}

	/**
	 * Finds a crumb index by the name of the crumb
	 * @param key Keyname of the crumb to find
	 */
	findCrumbIndexByKey(key: string) {
		console.log();
		for (let i = 0; i < this.breadcrumbs.length; i++) {
			console.log("finding crumb by key", key, this.breadcrumbs[i]);
			if (key === this.breadcrumbs[i].key) {
				return i;
			}
		}
		return null;
	}

	/**
	 * Sends an HTTP GET to a download route with form value params
	 * Downloads the received document using the form submit method in a new tab.
	 * @param targetURL Download URL
	 * @param elems Array of Params to send with the request
	 */
	public BuildHiddenFormForDownload(
		targetURL: string,
		elems: Array<KeyValuePair<string, string>>
	): HTMLFormElement {
		var form = document.createElement("form");
		form.method = "get";
		form.target = "_blank";
		form.action = targetURL;

		elems.forEach((e) => {
			var input = document.createElement("input");
			input.setAttribute("type", "hidden");
			input.setAttribute("name", e.key);
			input.setAttribute("value", encodeURI(e.value));
			form.appendChild(input);
		});
		return form;
	}

	/**
	 * Returns a summary of Residuals for the given SalesEntity, summarized by Agent
	 * @param salesEntityID SE_ID
	 * @param commissionsDate Date of Commissions
	 */
	public GetResidualsAgentSummaryAll(
		salesEntityID: number,
		commissionsDate: Date
	): Promise<Array<ResidualsAgentSummary>> {
		const url = this.controller + "GetResidualsAgentSummaryAll";
		const parms = new HttpParams()
			.set("salesEntityID", salesEntityID.toString())
			.append(
				"commissionsDate",
				encodeURI(this.BuildDateString(commissionsDate))
			);
		const observable = this.cacheService.get(
			url + parms,
			this.http.get(url, { params: parms })
		);
		return observable.toPromise();
	}
} // class
